import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  Search,
  Wrench,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  FileText,
  RefreshCw,
  Eye,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TokenRepairPage = () => {
  const { user } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [selectedNotes, setSelectedNotes] = useState(new Set());
  const [repairing, setRepairing] = useState(false);
  const [repairResults, setRepairResults] = useState(null);
  const [showRepairDialog, setShowRepairDialog] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState(new Set());

  // Check if user is system admin
  if (user?.role !== 'system_admin') {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            This page is only accessible to System Administrators.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const handleScan = async () => {
    setScanning(true);
    setScanResults(null);
    setSelectedNotes(new Set());
    setRepairResults(null);
    
    try {
      const response = await axios.get(`${API}/admin/token-repair/scan`);
      setScanResults(response.data);
      
      // Auto-select all auto-repairable notes
      const autoRepairable = response.data.affected_notes
        .filter(n => n.can_auto_repair && n.resolvable_changes.length > 0)
        .map(n => n.note_id);
      setSelectedNotes(new Set(autoRepairable));
      
      if (response.data.affected_notes_count === 0) {
        toast.success('No notes with unresolved tokens found!');
      } else {
        toast.info(`Found ${response.data.affected_notes_count} notes with unresolved tokens`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleRepair = async () => {
    if (selectedNotes.size === 0) {
      toast.error('No notes selected for repair');
      return;
    }
    
    setRepairing(true);
    
    try {
      const response = await axios.post(`${API}/admin/token-repair/execute`, {
        note_ids: Array.from(selectedNotes)
      });
      
      setRepairResults(response.data);
      setShowRepairDialog(false);
      
      if (response.data.repaired_count > 0) {
        toast.success(`Successfully repaired ${response.data.repaired_count} notes`);
      }
      
      if (response.data.skipped_count > 0) {
        toast.warning(`${response.data.skipped_count} notes skipped (require manual review)`);
      }
      
      // Re-scan to show updated state
      handleScan();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Repair failed');
    } finally {
      setRepairing(false);
    }
  };

  const toggleNoteSelection = (noteId) => {
    const newSelected = new Set(selectedNotes);
    if (newSelected.has(noteId)) {
      newSelected.delete(noteId);
    } else {
      newSelected.add(noteId);
    }
    setSelectedNotes(newSelected);
  };

  const toggleSelectAll = () => {
    if (!scanResults) return;
    
    const autoRepairable = scanResults.affected_notes
      .filter(n => n.can_auto_repair && n.resolvable_changes.length > 0)
      .map(n => n.note_id);
    
    if (selectedNotes.size === autoRepairable.length) {
      setSelectedNotes(new Set());
    } else {
      setSelectedNotes(new Set(autoRepairable));
    }
  };

  const toggleExpanded = (noteId) => {
    const newExpanded = new Set(expandedNotes);
    if (newExpanded.has(noteId)) {
      newExpanded.delete(noteId);
    } else {
      newExpanded.add(noteId);
    }
    setExpandedNotes(newExpanded);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wrench className="w-6 h-6" />
            Token Repair Tool
          </h1>
          <p className="text-muted-foreground mt-1">
            Find and repair case notes with unresolved merge tokens from Mass Case Note Entry
          </p>
        </div>
        
        <Button onClick={handleScan} disabled={scanning}>
          {scanning ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Scanning...
            </>
          ) : (
            <>
              <Search className="w-4 h-4 mr-2" />
              Scan for Unresolved Tokens
            </>
          )}
        </Button>
      </div>

      {/* Scan Results Summary */}
      {scanResults && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Scanned</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{scanResults.total_notes_scanned}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Affected Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{scanResults.affected_notes_count}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Can Auto-Repair</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{scanResults.can_auto_repair_count}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Need Manual Review</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{scanResults.needs_manual_review_count}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Repair Results */}
      {repairResults && (
        <Alert className={repairResults.repaired_count > 0 ? "border-green-500 bg-green-50" : "border-orange-500 bg-orange-50"}>
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertTitle>Repair Complete</AlertTitle>
          <AlertDescription>
            <div className="mt-2 space-y-1">
              <p><strong>{repairResults.repaired_count}</strong> notes repaired successfully</p>
              {repairResults.skipped_count > 0 && (
                <p className="text-orange-600"><strong>{repairResults.skipped_count}</strong> notes skipped (require manual review)</p>
              )}
              {repairResults.error_count > 0 && (
                <p className="text-red-600"><strong>{repairResults.error_count}</strong> errors occurred</p>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Affected Notes Table */}
      {scanResults && scanResults.affected_notes.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Affected Notes ({scanResults.affected_notes.length})
                </CardTitle>
                <CardDescription>
                  Select notes to repair. Notes with unresolved Excel tokens require manual review.
                </CardDescription>
              </div>
              
              {scanResults.can_auto_repair_count > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {selectedNotes.size} selected
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={toggleSelectAll}
                  >
                    {selectedNotes.size === scanResults.can_auto_repair_count ? 'Deselect All' : 'Select All Repairable'}
                  </Button>
                  <Button 
                    onClick={() => setShowRepairDialog(true)}
                    disabled={selectedNotes.size === 0}
                  >
                    <Wrench className="w-4 h-4 mr-2" />
                    Repair Selected ({selectedNotes.size})
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Note ID</TableHead>
                    <TableHead>Participant</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Tokens Found</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scanResults.affected_notes.map((note) => (
                    <React.Fragment key={note.note_id}>
                      <TableRow className={!note.can_auto_repair ? 'bg-red-50' : ''}>
                        <TableCell>
                          {note.can_auto_repair && note.resolvable_changes.length > 0 && (
                            <Checkbox
                              checked={selectedNotes.has(note.note_id)}
                              onCheckedChange={() => toggleNoteSelection(note.note_id)}
                              data-testid={`select-note-${note.note_id}`}
                            />
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{note.note_id}</TableCell>
                        <TableCell>
                          <div className="font-medium">{note.participant_name}</div>
                          <div className="text-xs text-muted-foreground">{note.participant_id}</div>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate" title={note.subject_preview}>
                          {note.subject_preview}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {[...new Set([...note.subject_tokens, ...note.body_tokens])].slice(0, 3).map((token, i) => (
                              <Badge 
                                key={i} 
                                variant="outline" 
                                className={token.startsWith('excel_') ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}
                              >
                                {token}
                              </Badge>
                            ))}
                            {[...new Set([...note.subject_tokens, ...note.body_tokens])].length > 3 && (
                              <Badge variant="outline">+{[...new Set([...note.subject_tokens, ...note.body_tokens])].length - 3} more</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {note.can_auto_repair ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Can Repair
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Manual Review
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleExpanded(note.note_id)}
                          >
                            {expandedNotes.has(note.note_id) ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded Details */}
                      {expandedNotes.has(note.note_id) && (
                        <TableRow>
                          <TableCell colSpan={7} className="bg-muted/50">
                            <div className="p-4 space-y-4">
                              <div>
                                <h4 className="font-medium mb-2">Proposed Changes:</h4>
                                {note.resolvable_changes.length > 0 ? (
                                  <div className="space-y-1">
                                    {note.resolvable_changes.map((change, i) => (
                                      <div key={i} className="text-sm flex items-center gap-2">
                                        <Badge variant="outline" className="text-xs">{change.field}</Badge>
                                        <code className="text-red-600 bg-red-50 px-1 rounded">{change.original}</code>
                                        <span>→</span>
                                        <code className="text-green-600 bg-green-50 px-1 rounded">{change.resolved_to}</code>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-muted-foreground">No resolvable changes</p>
                                )}
                              </div>
                              
                              {note.unresolved_tokens.length > 0 && (
                                <div>
                                  <h4 className="font-medium mb-2 text-red-600">Unresolved Tokens (Manual Review Required):</h4>
                                  <div className="flex flex-wrap gap-1">
                                    {note.unresolved_tokens.map((token, i) => (
                                      <Badge key={i} variant="destructive">{token}</Badge>
                                    ))}
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-2">
                                    These tokens reference Excel data that is no longer available. 
                                    You&apos;ll need to manually edit this note.
                                  </p>
                                </div>
                              )}
                              
                              <div>
                                <h4 className="font-medium mb-2">Body Preview:</h4>
                                <p className="text-sm text-muted-foreground bg-white p-2 rounded border">
                                  {note.body_preview}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* No Affected Notes */}
      {scanResults && scanResults.affected_notes.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-4" />
            <h3 className="text-lg font-medium">All Clear!</h3>
            <p className="text-muted-foreground">No case notes with unresolved tokens were found.</p>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showRepairDialog} onOpenChange={setShowRepairDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Token Repair</DialogTitle>
            <DialogDescription>
              You are about to repair <strong>{selectedNotes.size}</strong> case notes.
              This action will replace unresolved merge tokens with actual participant and case data.
              All changes will be logged to the audit trail.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRepairDialog(false)} disabled={repairing}>
              Cancel
            </Button>
            <Button onClick={handleRepair} disabled={repairing}>
              {repairing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Repairing...
                </>
              ) : (
                <>
                  <Wrench className="w-4 h-4 mr-2" />
                  Confirm Repair
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TokenRepairPage;
